import { Dynamic } from "solid-js/web";
import { cn } from "@time-tracker/shared";

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
  /**
   * Element tag for the wordmark. Defaults to "h1" so the marketing/standalone
   * logo owns the page heading. Pass "p" when the logo sits inside a surface
   * whose own title already provides the page `h1` (e.g. a surface whose
   * heading carries the single h1).
   */
  as?: "h1" | "p";
}

/**
 * ChronosTracker's own mark: a stopwatch with the hand in motion
 * ("time running") drawn in `currentColor`. It is ALWAYS rendered
 * over a SOLID `bg-accent` chip with the mark in `text-accent-foreground`,
 * the system's guaranteed contrast pair.
 *
 * Historical gotcha (it broke GymLog / bso / field-check): painting the mark with
 * `text-accent` / `bg-accent/12` / a color over a faint surface made the
 * mark invisible or below AA in themes with low-contrast accents.
 * Here the mark inherits `accent-foreground` (it does not depend on the accent hue) and the
 * wordmark uses `text-foreground` / `text-muted-foreground`.
 */
function ChronosMark(props: { class?: string }) {
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class={props.class}
      aria-hidden="true"
    >
      {/* botón superior del cronómetro */}
      <rect x="13.25" y="1.5" width="5.5" height="2.6" rx="1.1" fill="currentColor" />
      <rect x="14.6" y="3.6" width="2.8" height="2.4" rx="0.9" fill="currentColor" />
      {/* esfera */}
      <circle cx="16" cy="18.5" r="11" stroke="currentColor" stroke-width="2.1" />
      {/* marcas de las horas (12 / 6 / 9 / 3) */}
      <line
        x1="16"
        y1="9"
        x2="16"
        y2="11"
        stroke="currentColor"
        stroke-width="1.7"
        stroke-linecap="round"
        opacity="0.75"
      />
      <line
        x1="16"
        y1="26"
        x2="16"
        y2="28"
        stroke="currentColor"
        stroke-width="1.7"
        stroke-linecap="round"
        opacity="0.75"
      />
      <line
        x1="6.5"
        y1="18.5"
        x2="8.5"
        y2="18.5"
        stroke="currentColor"
        stroke-width="1.7"
        stroke-linecap="round"
        opacity="0.75"
      />
      <line
        x1="23.5"
        y1="18.5"
        x2="25.5"
        y2="18.5"
        stroke="currentColor"
        stroke-width="1.7"
        stroke-linecap="round"
        opacity="0.75"
      />
      {/* manecillas — mismo currentColor (contraste garantizado) */}
      <path d="M16 18.5 L16 12" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
      <path d="M16 18.5 L20.5 21" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
      <circle cx="16" cy="18.5" r="1.7" fill="currentColor" />
    </svg>
  );
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";

  return (
    <div
      class={cn(
        "flex items-center gap-3 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <div class="h-9 w-9 rounded-xl bg-accent text-accent-foreground shadow-sm ring-1 ring-black/[0.06] flex items-center justify-center shrink-0">
            <ChronosMark class="h-6 w-6" />
          </div>
          <span class="text-xl font-bold tracking-tight text-foreground">
            Chronos
            <span class="text-muted-foreground font-semibold">Tracker</span>
          </span>
        </>
      ) : (
        <>
          <div class="h-16 w-16 rounded-2xl bg-accent text-accent-foreground shadow-md ring-1 ring-black/[0.06] flex items-center justify-center mb-2 shrink-0 transition-transform hover:scale-105 active:scale-95 duration-300">
            <ChronosMark class="h-10 w-10" />
          </div>
          <Dynamic
            component={props.as ?? "h1"}
            class="text-3xl font-bold tracking-tight text-foreground"
          >
            Chronos<span class="text-muted-foreground font-semibold">Tracker</span>
          </Dynamic>
        </>
      )}
    </div>
  );
}
